import ReactPlayer from "react-player";
import { useState, useRef, useEffect } from "react";
import { ExclamationCircleIcon, PauseIcon, PlayIcon } from "@heroicons/react/20/solid";
import { formatSecondsToMinutesSeconds } from "../../helpers/date/date-helper";

export const GeneralVideoPlayer = ({ videoUrl, handleOnProgress, handleVideoLoadedWithDuration, handleOnError, startPlaying = true, startProgress = null, command = null, markers = [], OverlayComponent = null, overlayProps = {} }) => {

  const playerRef = useRef(null);
  const playerContainerRef = useRef(null);
  const currentTimeRef = useRef(0);

  const [duration, setDuration] = useState(null);
  const [videoRect, setVideoRect] = useState(null);

  const [errorMessage, setErrorMessage] = useState(null);

  const [playing, setPlaying] = useState(startPlaying);
  const [progress, setProgress] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const progressRef = useRef(null);
  const [isProgressDragging, setIsProgressDragging] = useState(false);

  const onDuration = (duration) => {
    setDuration(duration);

    if (handleVideoLoadedWithDuration !== null && handleVideoLoadedWithDuration !== undefined)
      handleVideoLoadedWithDuration(duration);
  };

  const onProgress = (progress) => {
    if (handleOnProgress !== null && handleOnProgress !== undefined)
      handleOnProgress(progress);

    currentTimeRef.current = progress.playedSeconds;
    setProgress(progress.played);
  };

  const updateProgress = (progress) => {
    if (playerRef.current){
      playerRef.current.seekTo(progress);
    }
  };

  const onError = (error) => {
    console.log(error);
    setErrorMessage('Une erreur est survenue lors du chargement de la vidéo.');
  };

  const handlePlayPause = (e) => {
    e.stopPropagation();
    setPlaying((prev) => !prev);
  };

  const handleProgressMouseDown = (e) => {
    setIsProgressDragging(true);
    handleProgressClick(e);
  };

  const handleProgressMouseMove = (e) => {
    if (isProgressDragging) {
      handleProgressClick(e);
    }
  };

  const handleProgressMouseUp = () => {
    setIsProgressDragging(false);
  };

  const handleProgressClick = (e) => {
    const rect = progressRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const newTimePercent = Math.min(Math.max(0, (offsetX / rect.width)), 1);
    updateProgress(newTimePercent);
  };

  useEffect(() => {
    if (isProgressDragging) {
      document.addEventListener('mousemove', handleProgressMouseMove);
      document.addEventListener('mouseup', handleProgressMouseUp);
    } else {
      document.removeEventListener('mousemove', handleProgressMouseMove);
      document.removeEventListener('mouseup', handleProgressMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleProgressMouseMove);
      document.removeEventListener('mouseup', handleProgressMouseUp);
    };
  }, [isProgressDragging]);

  const onVideoLoaded = () => {
    if (!isReady && startProgress !== null){
      setProgress(startProgress);
      updateProgress(startProgress);
    }

    setIsReady(true);
    updateVideoRect();
  };

  /* Keyboard shortcuts */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isReady) return;

      const target = e.target;
      const tagName = target?.tagName?.toLowerCase();
      const isEditable =
        tagName === "input" ||
        tagName === "textarea" ||
        target?.isContentEditable === true;

      if (isEditable) return;

      switch (e.key) {
        case ' ':
          e.preventDefault();
          setPlaying((prev) => !prev);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          updateProgress(Math.max(0, progress - 0.1));
          break;
        case 'ArrowRight':
          e.preventDefault();
          updateProgress(Math.min(1, progress + 0.1));
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isReady, progress]);

  /* Commands */
  useEffect(() => {
    if (!command) return;
    if (!playerRef.current) return;

    switch (command.type) {
      case "PAUSE_SEEK_SECONDS": {
        setPlaying(false);
        playerRef.current.seekTo(command.seconds ?? 0, "seconds");
        break;
      }
      case "SEEK_SECONDS": {
        playerRef.current.seekTo(command.seconds ?? 0, "seconds");
        break;
      }
      case "PAUSE": {
        setPlaying(false);
        break;
      }
      case "PLAY": {
        setPlaying(true);
        break;
      }
      default:
        break;
    }
  }, [command?.nonce]);

  /* Resize observer */
  useEffect(() => {
    if (!isReady) return;

    updateVideoRect();

    const resizeObserver = new ResizeObserver(updateVideoRect);

    if (playerContainerRef.current) {
      resizeObserver.observe(playerContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [isReady]);

  const updateVideoRect = () => {
    const container = playerContainerRef.current;
    const video = playerRef.current?.getInternalPlayer?.();

    if (!container || !video?.videoWidth || !video?.videoHeight) return;

    const containerRect = container.getBoundingClientRect();
    const containerRatio = containerRect.width / containerRect.height;
    const videoRatio = video.videoWidth / video.videoHeight;

    let width = containerRect.width;
    let height = containerRect.height;
    let left = 0;
    let top = 0;

    if (containerRatio > videoRatio) {
      width = containerRect.height * videoRatio;
      left = (containerRect.width - width) / 2;
    } else {
      height = containerRect.width / videoRatio;
      top = (containerRect.height - height) / 2;
    }

    setVideoRect({ left, top, width, height });
  };

  return (
    <>
      {
        errorMessage ?
          <>
            <div className='w-full h-full flex flex-col justify-center items-center text-center'>
              <ExclamationCircleIcon className="w-16 h-16 text-error"/>
              <div className='text-error'>{errorMessage}</div>
            </div>
          </>
        :
          <div ref={playerContainerRef} className="relative h-full w-full bg-black rounded-md overflow-hidden" onClick={handlePlayPause}>
            <ReactPlayer
              ref={playerRef}
              url={videoUrl}
              controls={false}
              playing={playing}
              onDuration={onDuration}
              onProgress={onProgress}
              progressInterval={100}
              width="100%"
              height="100%"
              onReady={onVideoLoaded}
              onError={onError}
              muted={true}
            />
            {
              // Controls
              isReady &&
                <div className="absolute bottom-2 left-2 right-2 hover:backdrop-blur-lg hover:bg-gray-500/20 transition-all rounded-md z-30 flex items-center gap-4 px-4 py-2 group">
                  <button className="bg-perception-blue p-2 rounded-full transition-transform duration-300 hover:scale-110" onClick={handlePlayPause}>
                    {
                      playing ?
                        <PauseIcon className="w-4 h-4 text-white"/> :
                        <PlayIcon className="w-4 h-4 text-white"/>
                    }
                  </button>

                  <div className="flex-1 h-2 backdrop-blur-md bg-white/30 rounded-full overflow-visible cursor-pointer transition-transform duration-300 group-hover:scale-y-150 relative"
                    ref={progressRef} onClick={(e) => e.stopPropagation()} onMouseDown={handleProgressMouseDown}>
                    <div className="h-full bg-perception-blue rounded-full transition-all duration-100 ease-linear" style={{ width: `${progress * 100}%` }}/>

                    {duration && markers.filter((marker) => marker >= 0 && marker <= duration).map((marker, markerIndex) => (
                      <div
                        key={markerIndex}
                        className="absolute top-1/2 h-4 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white z-20"
                        style={{ left: `${(marker / duration) * 100}%` }}
                      />
                    ))
                    }

                    <div className="absolute top-1/2 -translate-y-[10px] z-30 transition-all duration-100 ease-linear" style={{ left: `${progress * 100}%` }}>
                      <button type="button" className="w-4 max-h-4 h-4 rounded-full bg-white shadow-md -translate-x-1/2 transition-transform duration-300 group-hover:scale-x-[1.5] cursor-pointer select-none"
                              onMouseDown={(e) => { e.stopPropagation(); setIsProgressDragging(true); handleProgressClick(e) }}
                        onClick={(e) => e.stopPropagation()}
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                        aria-label="Seek"
                      />
                    </div>
                  </div>

                  <div className="text-white text-xs">
                    {formatSecondsToMinutesSeconds(progress * duration)}/{formatSecondsToMinutesSeconds(duration)}
                  </div>
                </div>
            }

            {/* Overlay */}
            <div className="absolute top-0 right-0 bottom-0 left-0 z-10">
              {OverlayComponent ? (
                <OverlayComponent
                  {...overlayProps}
                  duration={duration}
                  currentTimeRef={currentTimeRef}
                  videoRect={videoRect}
                />
              ) : null}
            </div>
          </div>
      }
    </>
  );
};